import React from 'react';
import { MapPin, MessageCircle, MousePointerClick } from 'lucide-react';
import { BrandMark } from './BrandMark';

const WA_NUMBER = process.env.REACT_APP_SUPPORT_WA_NUMBER || '';
const WA_PREFILL = encodeURIComponent("Hi Luggagefreejourney team, I need help with my booking.");

/**
 * Shared dark footer for the public marketing pages. Designed for
 * editorial-brutalism aesthetic (cream/dark contrast, mono labels, sharp
 * border accents).
 */
export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="public-footer"
      className="text-[#1A1A1A] mt-8 sm:mt-10 relative"
      style={{ background: '#E8DCC9' }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-10 sm:pt-12 pb-8 sm:pb-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand + tagline */}
        <div className="md:col-span-7">
          <BrandMark size={40} textClass="text-xl text-[#1A1A1A]" />
          <p className="mt-6 text-sm leading-relaxed text-[#525252] max-w-md font-bold text-[#1A1A1A]">
            Travel Light. Travel Smart.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-[#525252] max-w-md">
            LuggageFreeJourney is designed for travellers, and travel is redefined by
            making luggage transportation simple, secure, and stress-free. We pick up
            your bags from your doorstep and deliver them safely to your destination,
            allowing you to travel comfortably without the burden of heavy luggage.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-[#525252] max-w-md">
            With reliable tracking, secure handling, and on-time delivery,
            Luggagefreejourney is committed to giving every traveler a seamless journey.
            Whether you&apos;re traveling for business, leisure, education, or relocation,
            LuggageFreeJourney ensures your bags arrive safely — so you can focus on
            enjoying the journey, not carrying it.
          </p>
        </div>

        {/* Contact */}
        <div className="md:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A1F] mb-4 font-bold">
            Contact Us
          </div>
          <ul className="space-y-3 text-sm">
            {WA_NUMBER && (
              <li>
                <a
                  href={`https://wa.me/${WA_NUMBER}?text=${WA_PREFILL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-cta"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-full hover:bg-[#1FB855] transition-colors no-underline"
                >
                  <MessageCircle size={14} />
                  Chat on WhatsApp
                </a>
              </li>
            )}
            <li>
              <a
                href="/support"
                data-testid="footer-support-form"
                className="group inline-flex items-center gap-2 bg-white border-2 border-black text-[#1A1A1A] font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-[#FF5A1F] hover:text-white transition-colors no-underline"
              >
                <MousePointerClick size={14} className="group-hover:animate-pulse" />
                Send message
                <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
              </a>
            </li>
            <li className="inline-flex items-center gap-3 text-[#737373]">
              <MapPin size={16} className="flex-shrink-0" />
              <span>Support hours: Mon–Sat, 9 AM–7 PM IST</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between gap-8 sm:gap-4 text-xs">
          <span className="uppercase tracking-[0.25em] text-[#737373] text-[10px] sm:text-xs">© {year} Luggagefreejourney</span>
          <div className="flex flex-nowrap items-center gap-x-2 sm:gap-x-6 text-[#1A1A1A] uppercase tracking-[0.03em] sm:tracking-[0.2em] text-[11px] sm:text-xs font-bold whitespace-nowrap">
            <a href="/about" data-testid="footer-link-about" className="hover:text-[#FF5A1F] transition-colors">About us</a>
            <span aria-hidden="true" className="text-[#1A1A1A]/20">|</span>
            <a href="/terms" data-testid="footer-link-terms" className="hover:text-[#FF5A1F] transition-colors">Terms &amp; Conditions</a>
            <span aria-hidden="true" className="text-[#1A1A1A]/20">|</span>
            <a href="/policies" data-testid="footer-link-policies" className="hover:text-[#FF5A1F] transition-colors">Company Policies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
